import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Drawer,
  IconButton,
  styled,
  Stack,
  useTheme,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import useResponsive from '../../../hooks/useResponsive';
import CustomModal from '../../CustomModal';
import MalaysiaFlag from '../../../assets/profile/flag.png';
import Iconify from '../../Iconify';

const OtpStack = styled(Stack)(({ theme }) => ({
  justifyContent: 'center',
  gap: '12px',
}));

const ResendText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  marginTop: 4,
  color: '#919EAB',
  textAlign: 'right',
  width: '100%',
  cursor: 'pointer',
  '& span': {
    fontWeight: 600,
  },
}));

const OtpInput = styled('input')(({ theme }) => ({
  width: '100%',
  maxWidth: {
    md: '52px',
    xs: '46px',
  },
  aspectRatio: '1 / 1',
  borderRadius: '12px',
  border: 'none',
  background: '#F4F6F8',
  textAlign: 'center',
  fontSize: '20px',
  fontWeight: 600,
  color: '#212B36',
  outline: 'none',

  [theme.breakpoints.down('sm')]: {
    maxWidth: {
      md: '42px',
      xs: '47px',
    },
    fontSize: '18px',
  },

  '&:focus': {
    background: '#FFF',
    border: '1px solid #1E71FF',
  },

  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
}));

const MobileNumberDialog = ({ handleClose, handleOpen }) => {
  const isDesktop = useResponsive('up', 'lg');
  const theme = useTheme();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [phone, setPhone] = useState('');
  const [currentStep, setCurrentStep] = useState('phone'); // <--- Track which step

  const dispatch = useDispatch();

  const cleanedPhone = phone.replace(/\s/g, '');
  const isValidPhone = /^1\d{8,9}$/.test(cleanedPhone);
  const isError = phone.length > 0 && !isValidPhone;

  const handleOtpChange = (element, index) => {
    if (Number.isNaN(Number(element.value))) return false;

    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    if (element.nextSibling && element.value !== '') {
      element.nextSibling.focus();
    }
  };

  const maskPhone = (number) => {
    if (!number) return '';

    const clean = number.replace(/\D/g, '');
    if (clean.length < 4) return clean;

    const first3 = clean.slice(0, 3);
    const last2 = clean.slice(-2);

    return `${first3}****${last2}`;
  };

  // Before the return
  const isSubmitDisabled = currentStep === 'phone' ? !isValidPhone : otp.some((digit) => digit === '');

  const buttonBackground = isSubmitDisabled ? theme.palette.action.disabledBackground : theme.palette.gradients.buttonPrimary;

  const buttonColor = isSubmitDisabled ? theme.palette.action.disabled : theme.palette.common.white;

  const buttonHover = isSubmitDisabled ? theme.palette.action.disabledBackground : theme.palette.gradients.hover;

  return (
    <>
      {isDesktop && (
        <CustomModal
          hasClose
          maxWidth="xs"
          open={handleOpen}
          onClose={handleClose}
          actions={
            <Button
              variant="contained"
              disabled={isSubmitDisabled}
              fullWidth
              sx={{
                textTransform: 'none',
                borderRadius: '25px',
                boxShadow: 'none',
                height: 45,
                background: { buttonBackground },
                color: { buttonColor },
              }}
              onClick={() => {
                if (currentStep === 'phone') {
                  setCurrentStep('otp');
                } else {
                  console.log('submit otp');
                }
              }}
            >
              Submit
            </Button>
          }
        >
          {currentStep === 'phone' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2, px: 3 }}>
              <Typography variant="h6" fontWeight={600} textAlign="center">
                Phone Number
              </Typography>

              <Typography variant="body2" fontWeight={500}>
                Mobile
              </Typography>

              <TextField
                placeholder="00 000 0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                error={phone.length > 0 && !isValidPhone}
                helperText={
                  isError && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Iconify icon="mdi:alert-circle" width="16" />
                      Please enter the correct Phone number
                    </span>
                  )
                }
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img
                          src={MalaysiaFlag}
                          alt="Malaysia"
                          style={{
                            width: 24,
                            height: 16,
                            objectFit: 'cover',
                            borderRadius: 2,
                          }}
                        />
                        <Typography fontSize={'16px'} variant="body2" sx={{ color: '#555', mr: 2 }}>
                          +60{' '}
                        </Typography>
                      </Box>
                    </InputAdornment>
                  ),

                  endAdornment: isError && (
                    <InputAdornment position="end">
                      <Iconify
                        icon="mdi:close-circle"
                        width={18}
                        sx={{ color: '#EF4444', cursor: 'pointer' }}
                        onClick={() => setPhone('')}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: isError ? '#FEE2E2' : '#F3F4F6',

                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: '1px solid #E0E0E0',
                    },
                  },

                  '& input': {
                    padding: '12px 8px',
                    paddingLeft: '6px', // 👈 add spacing after +60
                  },

                  '& input::placeholder': {
                    color: '#9CA3AF',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', py: 2, px: 3 }}>
              <Typography variant="h6" fontWeight={600} textAlign="center">
                Phone Number
              </Typography>

              <Box mt={4}>
                <Typography textAlign={'center'} variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#212B36' }}>
                  Enter The Code
                </Typography>
                <Typography textAlign={'center'} sx={{ color: '#637381', fontSize: '14px' }}>
                  We'll Send A Code To Your Mobile Number
                </Typography>
                <Typography textAlign="center" sx={{ fontWeight: 700, color: '#212B36', mb: 3 }}>
                  +60 {maskPhone(phone)}
                </Typography>
              </Box>

              <Box>
                <OtpStack direction="row">
                  {otp.map((data, index) => (
                    <OtpInput
                      key={index}
                      type="number"
                      maxLength="1"
                      value={data}
                      onChange={(e) => handleOtpChange(e.target, index)}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !otp[index] && e.target.previousSibling) {
                          e.target.previousSibling.focus();
                        }
                      }}
                    />
                  ))}
                </OtpStack>

                <ResendText>
                  Resend Code: <span>30S</span>
                </ResendText>
              </Box>
            </Box>
          )}
        </CustomModal>
      )}

      {!isDesktop && (
        <Drawer
          anchor="right"
          maxWidth="xs"
          open={handleOpen}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: '100%',
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              borderBottom: '1px solid #fff',
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                left: 16,
              }}
            >
              <Iconify icon="mdi:arrow-left" width={24} />
            </IconButton>

            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
              }}
            >
              Phone Number
            </Typography>
          </Box>
          {currentStep === 'phone' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                height: '100%',
                backgroundColor: '#F6F7F9',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  backgroundColor: '#fff',
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" fontWeight={600} textAlign="start">
                  Mobile
                </Typography>
                <TextField
                  placeholder="00 000 0000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  error={phone.length > 0 && !isValidPhone}
                  helperText={
                    isError && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Iconify icon="mdi:alert-circle" width="16" />
                        Please enter the correct Phone number
                      </span>
                    )
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img
                            src={MalaysiaFlag}
                            alt="Malaysia"
                            style={{
                              width: 24,
                              height: 16,
                              objectFit: 'cover',
                              borderRadius: 2,
                            }}
                          />
                          <Typography fontSize={'16px'} variant="body2" sx={{ color: '#555', mr: 2 }}>
                            +60{' '}
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),

                    endAdornment: isError && (
                      <InputAdornment position="end">
                        <Iconify
                          icon="mdi:close-circle"
                          width={18}
                          sx={{ color: '#EF4444', cursor: 'pointer' }}
                          onClick={() => setPhone('')}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: isError ? '#FEE2E2' : '#F3F4F6',

                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: '1px solid #E0E0E0',
                      },
                    },

                    '& input': {
                      padding: '12px 8px',
                      paddingLeft: '6px', // 👈 add spacing after +60
                    },

                    '& input::placeholder': {
                      color: '#9CA3AF',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
              <Button
                variant="contained"
                disabled={!isValidPhone}
                fullWidth
                onClick={() => {
                  if (currentStep === 'phone') {
                    setCurrentStep('otp');
                  } else {
                    console.log('submit otp');
                  }
                }}
                sx={(theme) => ({
                  textTransform: 'none',
                  background: !isValidPhone ? theme.palette.action.disabledBackground : theme.palette.gradients.buttonPrimary,
                  color: !isValidPhone ? theme.palette.action.disabled : theme.palette.common.white,
                  borderRadius: '25px',
                  boxShadow: 'none',
                  height: 45,
                  mt: '30px',
                })}
              >
                Submit
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                height: '100%',
                backgroundColor: '#F6F7F9',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  backgroundColor: '#fff',
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Box>
                  <Typography
                    textAlign={'center'}
                    fontSize={'24px'}
                    variant="h5"
                    sx={{ fontWeight: 700, mb: 1, color: '#212B36' }}
                  >
                    Enter The Code
                  </Typography>
                  <Typography textAlign={'center'} sx={{ color: '#637381', fontSize: '14px' }}>
                    We'll Send A Code To Your Mobile Number
                  </Typography>
                  <Typography textAlign="center" sx={{ fontWeight: 700, color: '#212B36', mb: 3 }}>
                    +60 {maskPhone(phone)}
                  </Typography>
                </Box>

                <Box>
                  <OtpStack direction="row">
                    {otp.map((data, index) => (
                      <OtpInput
                        key={index}
                        type="number"
                        maxLength="1"
                        value={data}
                        onChange={(e) => handleOtpChange(e.target, index)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !otp[index] && e.target.previousSibling) {
                            e.target.previousSibling.focus();
                          }
                        }}
                      />
                    ))}
                  </OtpStack>

                  <ResendText>
                    Resend Code: <span>30S</span>
                  </ResendText>
                </Box>

                <Button
                  variant="contained"
                  disabled={isSubmitDisabled}
                  fullWidth
                  onClick={() => {
                    if (currentStep === 'phone') {
                      setCurrentStep('otp');
                    } else {
                      console.log('submit otp');
                    }
                  }}
                  sx={(theme) => ({
                    textTransform: 'none',
                    background: { buttonBackground },
                    color: { buttonColor },
                    borderRadius: '25px',
                    boxShadow: 'none',
                    height: 45,
                    mt: '30px',
                  })}
                >
                  Done
                </Button>
              </Box>
            </Box>
          )}
        </Drawer>
      )}
    </>
  );
};

export default MobileNumberDialog;
